import { SearchOutlined } from "@ant-design/icons";
import { Col, Input, Row, Select, Typography, Form } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AppDispatch,
  dynamicSet,
  getClientOrganizationList,
  getClientOrganizationListRequest,
  useDynamicSelector,
} from "@src/redux";
import { useDispatch, useSelector } from "react-redux";
import { filterOptions } from "@src/helpers/constants";

const { Title, Text } = Typography;

const ClientFilters: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { filter: savedFilter } = useDynamicSelector("client_filter");
  const [search, setSearch] = useState<string>(
    savedFilter?.searchString || ""
  );
  const { result: clientOrganizationList } = useSelector(
    getClientOrganizationList
  );

  const initialPagination = { pageNumber: 1, pageLimit: 10 };
  const isInitialLoad = useRef(true);

  // Sync form fields when savedFilter changes (e.g. back-navigation)
  useEffect(() => {
    form.setFieldsValue({
      type: savedFilter?.type || undefined,
      status: savedFilter?.status || undefined,
      slaStatus: savedFilter?.slaStatus || undefined,
    });
    setSearch(savedFilter?.searchString || "");
  }, [form, savedFilter]);

  const triggerFilterRequest = useCallback(
    (filters: Record<string, any>, pagination = initialPagination) => {
      dispatch(dynamicSet("client_filter", { filter: filters }));
      dispatch(dynamicSet("client_pagination", { pagination }));
      dispatch(
        getClientOrganizationListRequest({
          filter: filters,
          sort: [{ field: "name", order: "asc" }],
          pagination,
        })
      );
    },
    [dispatch]
  );

  const buildFilters = useCallback(
    (
      formValues: Record<string, any>,
      searchOverride?: string
    ): Record<string, any> => {
      const { type, status, slaStatus } = formValues;
      const filters: Record<string, any> = {};

      const searchValue =
        searchOverride !== undefined ? searchOverride : search.trim();
      if (searchValue) filters.searchString = searchValue;
      if (type && type !== "all") filters.type = type;
      if (status && status !== "all") filters.status = status;
      if (slaStatus && slaStatus !== "all") filters.slaStatus = slaStatus;

      return filters;
    },
    [search]
  );

  // Fires on every form field change (dropdowns)
  const handleFilterChange = useCallback(
    (_changedValues: any, allValues: Record<string, any>) => {
      triggerFilterRequest(buildFilters(allValues));
    },
    [triggerFilterRequest, buildFilters]
  );

  const handleSearchEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value.trim();
    setSearch(value);
    triggerFilterRequest(buildFilters(form.getFieldsValue(), value));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (value.trim() === "") {
      triggerFilterRequest(buildFilters(form.getFieldsValue(), ""));
    }
  };

  // Initial data load
  useEffect(() => {
    if (!isInitialLoad.current) return;
    isInitialLoad.current = false;

    const initialFilters: Record<string, any> = {};
    if (savedFilter?.searchString) initialFilters.searchString = savedFilter.searchString;
    if (savedFilter?.type) initialFilters.type = savedFilter.type;
    if (savedFilter?.status) initialFilters.status = savedFilter.status;
    if (savedFilter?.slaStatus) initialFilters.slaStatus = savedFilter.slaStatus;

    triggerFilterRequest(initialFilters, initialPagination);
  }, []);

  return (
    <div className="filter-container">
      <div>
        <div className="header-filter">
          <Title level={4} className="client-filters-header-title">
            Filters
          </Title>
        </div>
        <Form
          form={form}
          onValuesChange={handleFilterChange}
          layout="vertical"
          className="common-filters"
        >
          <Form.Item label="Search">
            <Input
              prefix={<SearchOutlined />}
              className="filter-input"
              allowClear
              placeholder="Search clients..."
              value={search}
              onChange={handleSearchChange}
              onPressEnter={handleSearchEnter}
            />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Select
              options={filterOptions.clientType}
              allowClear
              placeholder="All"
              className="custom-placeholder"
            />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select
              options={filterOptions.clientStatus}
              allowClear
              placeholder="All"
              className="custom-placeholder"
            />
          </Form.Item>
          <Form.Item name="slaStatus" label="SLA">
            <Select
              options={filterOptions?.slaOptions}
              allowClear
              placeholder="All"
              className="custom-placeholder"
            />
          </Form.Item>
        </Form>
      </div>
      <Row className="footer" align="middle">
        <Col>
          <Text strong style={{ fontSize: "16px", fontFamily: "Abel", color: "#555" }}>
            TOTAL
          </Text>
        </Col>
        <Col className="total-value">
          <span
            style={{ color: "#ffffff", fontSize: "14px", fontWeight: "bold" }}
          >
            {clientOrganizationList?.pagination?.totalCount || 0}
          </span>
        </Col>
      </Row>
    </div>
  );
};

export default ClientFilters;
